/**
 * Plant session-paint containment scope.
 *
 * Directions come from {@link ANCHOR_PARENT_ENDPOINT_BY_TYPE}; this module owns
 * only which edge families participate in Plant paint (the UI policy). Client
 * paint and the modifications placement-ancestry query import the same maps so
 * the two cannot drift.
 */
import type { RelationshipType } from '../generated/types/relationships'
import { ANCHOR_PARENT_ENDPOINT_BY_TYPE, type ParentEndpoint } from './anchor-parent-endpoints'

/**
 * Edge families whose parent chains drive Plant paint rollups and (with
 * ghost-only extras) ghost placement. Deliberately not the full anchor set —
 * `HAS_PART` / MES edges would inflate pills with entities no Plant tab renders.
 *
 * Order matters for ghost single-bucket precedence: earlier types win when a
 * deleted node has multiple parents (Factory edges before Area `LOCATED_IN`).
 */
export const PLANT_PAINT_CONTAINMENT_TYPES: readonly RelationshipType[] = [
  'HAS_LINE',
  'HAS_SUBLINE',
  'HAS_EQUIPMENT',
  'CONTROLS',
  'BELONGS_TO',
  'HAS_TAG',
  'HAS_SUBPROCESS',
  'PART_OF',
  'LOCATED_IN',
  'HOSTS',
  'HAS_JOB',
  'DEFINES',
  'HAS_TAG_PROVIDER',
  'HAS_DATABASE',
  'HAS_ALARM'
]

/**
 * Paint-only parent endpoints for edges that intentionally omit `anchors`.
 * `LOCATED_IN` places Equipment/Process/Part in an Area for the Areas tab, but
 * is deliberately not an Equipment placement anchor (Line via `HAS_EQUIPMENT`
 * remains the placement check).
 */
export const PLANT_PAINT_PARENT_ENDPOINT_OVERRIDES: Readonly<
  Partial<Record<RelationshipType, ParentEndpoint>>
> = {
  LOCATED_IN: 'end'
}

/**
 * Ghost-only extras — not used for rollup climb. Deleted Lines that
 * `INSTANTIATES` a Process sit under that Process's implementations grid.
 */
export const PLANT_PAINT_GHOST_ONLY_TYPES: readonly RelationshipType[] = ['INSTANTIATES']

export const PLANT_PAINT_GHOST_PARENT_ENDPOINT_OVERRIDES: Readonly<
  Partial<Record<RelationshipType, ParentEndpoint>>
> = {
  INSTANTIATES: 'end'
}

function endpointForType(
  type: RelationshipType,
  overrides: Readonly<Partial<Record<RelationshipType, ParentEndpoint>>>
): ParentEndpoint {
  const endpoint = overrides[type] ?? ANCHOR_PARENT_ENDPOINT_BY_TYPE[type]
  if (!endpoint) {
    throw new Error(
      `Plant paint containment type ${type} has no derived or override parent endpoint`
    )
  }
  return endpoint
}

function buildEndpointMap(
  types: readonly RelationshipType[],
  overrides: Readonly<Partial<Record<RelationshipType, ParentEndpoint>>>
): Readonly<Record<string, ParentEndpoint>> {
  const map: Record<string, ParentEndpoint> = {}
  for (const type of types) {
    map[type] = endpointForType(type, overrides)
  }
  return map
}

/** Parent endpoint map used for rollup climb. */
export const PLANT_PAINT_CONTAINMENT_PARENT_ENDPOINT: Readonly<Record<string, ParentEndpoint>> =
  buildEndpointMap(PLANT_PAINT_CONTAINMENT_TYPES, PLANT_PAINT_PARENT_ENDPOINT_OVERRIDES)

/** Parent endpoint map used for ghost placement (containment + ghost-only extras). */
export const PLANT_PAINT_GHOST_PARENT_ENDPOINT: Readonly<Record<string, ParentEndpoint>> = {
  ...PLANT_PAINT_CONTAINMENT_PARENT_ENDPOINT,
  ...buildEndpointMap(PLANT_PAINT_GHOST_ONLY_TYPES, PLANT_PAINT_GHOST_PARENT_ENDPOINT_OVERRIDES)
}

/** Ghost parent precedence: lower index wins when a deleted node has multiple parents. */
export const PLANT_PAINT_GHOST_TYPE_PRECEDENCE: ReadonlyMap<string, number> = new Map(
  [...PLANT_PAINT_CONTAINMENT_TYPES, ...PLANT_PAINT_GHOST_ONLY_TYPES].map((type, index) => [
    type,
    index
  ])
)

export interface PlantPaintAncestryEdgeTypes {
  /** Parent-at-start types for `()-[:T*1..8]->(n)`. */
  parentAtStart: readonly RelationshipType[]
  /** Parent-at-end types for `(n)-[:T*1..8]->()`. */
  parentAtEnd: readonly RelationshipType[]
}

/** Split the paint allowlist by derived direction for the placement-ancestry walk. */
export function plantPaintAncestryEdgeTypes(): PlantPaintAncestryEdgeTypes {
  const parentAtStart: RelationshipType[] = []
  const parentAtEnd: RelationshipType[] = []
  for (const type of PLANT_PAINT_CONTAINMENT_TYPES) {
    const endpoint = PLANT_PAINT_CONTAINMENT_PARENT_ENDPOINT[type]
    if (endpoint === 'start') parentAtStart.push(type)
    else parentAtEnd.push(type)
  }
  return { parentAtStart, parentAtEnd }
}

/**
 * Cypher fragment marker used by the modifications placement-ancestry query.
 * Specs match on this string when stubbing the graph read.
 */
export const PLANT_PAINT_PLACEMENT_ANCESTRY_MARKER = 'plantPaintPlacementAncestry'
